// src/app/auth/session-sync.service.ts
import { Injectable } from '@angular/core';
import { Subject, firstValueFrom, race, timer, map } from 'rxjs';

const CH_NAME = 'ammsas-auth';
const TOKEN_BUMP_KEY = 'ammsas_tokens_bumped_v1';
const REFRESH_LOCK_KEY = 'ammsas_refresh_lock_v1';
const LOGOUT_KEY = 'ammsas_logout_v1';

type RefreshLock = { tabId: string; until: number };

@Injectable({ providedIn: 'root' })
export class SessionSyncService {
    private channel?: BroadcastChannel;
    private tabId = crypto.randomUUID();

    readonly tokensUpdated$ = new Subject<void>();
    readonly logout$ = new Subject<void>();

    constructor() {
        // BroadcastChannel (wenn verfügbar)
        if ('BroadcastChannel' in window) {
            try {
                this.channel = new BroadcastChannel(CH_NAME);
                this.channel.onmessage = (e) => this.onMessage(e.data);
            } catch {
                // Ignorieren, Fallback übernimmt
            }
        }
        // storage-Fallback (feuert nur in *anderen* Tabs)
        window.addEventListener('storage', (e) => this.onStorage(e));
    }

    /** Andere Tabs informieren: Tokens wurden aktualisiert */
    broadcastTokensUpdated() {
        // storage-Fallback triggern
        localStorage.setItem(TOKEN_BUMP_KEY, String(Date.now()));
        this.post({ t: 'tokens' });
    }

    /** Andere Tabs informieren: Logout */
    broadcastLogout() {
        localStorage.setItem(LOGOUT_KEY, String(Date.now()));
        this.post({ t: 'logout' });
    }

    /** Cross-tab Refresh-Lock: true wenn Lock erfolgreich */
    tryAcquireRefreshLock(ms = 10_000): boolean {
        const now = Date.now();
        const raw = localStorage.getItem(REFRESH_LOCK_KEY);
        if (raw) {
            try {
                const lock: RefreshLock = JSON.parse(raw);
                if (lock.until > now) return false; // noch gesperrt
            } catch { /* ignore */ }
        }
        const mine: RefreshLock = { tabId: this.tabId, until: now + ms };
        localStorage.setItem(REFRESH_LOCK_KEY, JSON.stringify(mine));

        // Verify, dass *unser* Lock drinsteht (simple atomic check)
        const check = localStorage.getItem(REFRESH_LOCK_KEY);
        try {
            const curr: RefreshLock = JSON.parse(check!);
            return curr.tabId === this.tabId;
        } catch { return false; }
    }

    /** Refresh-Lock freigeben (nur wenn von uns) */
    releaseRefreshLock() {
        const raw = localStorage.getItem(REFRESH_LOCK_KEY);
        if (!raw) return;
        try {
            const curr: RefreshLock = JSON.parse(raw);
            if (curr.tabId === this.tabId) localStorage.removeItem(REFRESH_LOCK_KEY);
        } catch { /* ignore */ }
    }

    /**
     * Wartet bis ein anderer Tab Tokens bumped (true) oder Timeout (false).
     * (Fix gegenüber vorher: liefert jetzt wirklich false bei Timeout.)
     */
    async waitForTokensUpdate(timeoutMs = 8000): Promise<boolean> {
        const result = await firstValueFrom(race([
            this.tokensUpdated$.pipe(map(() => 'tokens' as const)),
            timer(timeoutMs).pipe(map(() => 'timeout' as const)),
        ]));
        return result === 'tokens';
    }

    // -------------- intern --------------
    private post(msg: any) {
        try { this.channel?.postMessage(msg); } catch { /* ignore */ }
    }

    private onMessage(msg: any) {
        if (!msg || !msg.t) return;
        if (msg.t === 'tokens') this.tokensUpdated$.next();
        if (msg.t === 'logout') this.logout$.next();
    }

    private onStorage(e: StorageEvent) {
        if (!e.key || e.newValue === e.oldValue) return;
        if (e.key === TOKEN_BUMP_KEY) this.tokensUpdated$.next();
        if (e.key === LOGOUT_KEY) this.logout$.next();
    }
}
